import { onMounted, computed, ref, type Ref, watch } from 'vue'
import { useDeviceType } from '~/composables/useDeviceType'

const THUMBS_SCROLL_OFFSET = 500

type ThumbnailControlsProps = {
  imageContainer: Ref<HTMLElement | null>
  thumbsContainer: Ref<HTMLElement | null>
}

export default ({
  imageContainer,
  thumbsContainer,
}: ThumbnailControlsProps) => {
  const { isDesktop } = useDeviceType()

  const thumbsOffset = ref(0)
  const thumbsHeight = ref(0)
  const imageHeight = ref(0)

  const allowScrollUp = computed(() => thumbsOffset.value > 0)
  const allowScrollDown = computed(
    () => thumbsOffset.value < maxThumbsOffset.value,
  )

  const setThumbsHeight = () => {
    thumbsHeight.value = thumbsContainer.value?.scrollHeight || 0
  }

  const setImageHeight = () => {
    imageHeight.value =
      imageContainer.value?.getBoundingClientRect().height || 0
  }

  const maxThumbsOffset = computed(() => {
    if (!imageContainer.value || !thumbsContainer.value) return 0
    return Math.max(0, thumbsHeight.value - imageHeight.value)
  })

  const thumbsOffsetStyle = computed(() => {
    return {
      transform: `translateY(-${thumbsOffset.value}px)`,
    }
  })

  const imageSizeTrackStyle = computed(() => {
    return {
      height: `${imageHeight.value}px`,
    }
  })

  const scrollThumbs = (direction: 'up' | 'down') => {
    if (direction === 'up') {
      thumbsOffset.value = Math.max(
        thumbsOffset.value - THUMBS_SCROLL_OFFSET,
        0,
      )
    } else {
      thumbsOffset.value = Math.min(
        thumbsOffset.value + THUMBS_SCROLL_OFFSET,
        maxThumbsOffset.value,
      )
    }
  }

  let thumbsObserver: ResizeObserver | null = null
  let imageObserver: ResizeObserver | null = null

  onMounted(() => {
    thumbsObserver = new ResizeObserver(setThumbsHeight)
    imageObserver = new ResizeObserver(setImageHeight)

    if (imageContainer.value && thumbsContainer.value) {
      thumbsObserver.observe(thumbsContainer.value)
      imageObserver.observe(imageContainer.value)
    }
  })

  onUnmounted(() => {
    thumbsObserver?.disconnect()
    imageObserver?.disconnect()
  })

  watch(isDesktop, () => {
    if (!isDesktop.value) {
      thumbsOffset.value = 0
    }
  })

  return {
    allowScrollUp,
    allowScrollDown,
    scrollThumbs,
    thumbsOffsetStyle,
    imageSizeTrackStyle,
  }
}
